import type { GetSuprSendUserBody } from "@nlc/shared-types";

import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { BellRing } from "lucide-react";

import { Button } from "@/components/button";
import { notificationsKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";

import { useUpdateCustomProperties } from "../../hooks/useUpdateCustomProperties";
import { BellOff } from "../icons/bellOff";
import { getPauseMessageFromUTC } from "../utils/getPauseMessage";
import { showToast, ToastTypeEnum } from "../utils/showToast";
import { Loading } from "./Loading";

export default function ResumeNotifications({
  user,
}: {
  user?: GetSuprSendUserBody;
}) {
  const update = useUpdateCustomProperties();
  const queryClient = useQueryClient();
  const { data } = useUser();
  const isLoading = useIsMutating({
    mutationKey: notificationsKeys.updateCustomProperties(),
  });

  if (!data?.user.email) return;

  if (
    !user?.properties.blockedHours ||
    !user?.properties.daysOff ||
    !data?.user
  ) {
    return;
  }

  const { daysOff } = user?.properties;
  const parsedDaysOff = JSON.parse(daysOff);

  const message = getPauseMessageFromUTC({
    daysOff: parsedDaysOff,
  });

  const handleResume = async () => {
    update.mutate({ key: "daysOff", value: JSON.stringify([]) });

    showToast("Notifications have resumed", ToastTypeEnum.SUCCESS);

    return await queryClient.invalidateQueries({
      queryKey: notificationsKeys.user(data?.user.email),
    });
  };

  if (parsedDaysOff.length === 0) return;

  return (
    <div className="flex justify-between items-center mb-5">
      <div className="flex items-center">
        <BellOff />
        <span className="text-button-secondary-fg font-semibold ml-3">
          {message}
        </span>
      </div>
      <Button className="w-52" onPress={handleResume} variant="secondary">
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <BellRing className="text-button-secondary-fg" />
            <span className="text-button-secondary-fg">
              Resume Notifications
            </span>
          </>
        )}
      </Button>
    </div>
  );
}
